import React, { useEffect, useState } from "react";
import { IDepartmentTree } from "redux/reducers/models/product";
import Checkbox from "components/form/checkbox/Checkbox";
import "../FilterDrawer.scss";
import { faChevronDown, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { ButtonNew } from "components/buttonNew";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";

interface ISectionProps {
  onSelect?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  section?: IDepartmentTree;
  depth?: number;
  isClosed?: boolean;
  type?: string;
  search?: string;
  deptIds: number[];
}
interface ISectionState {
  isSectionClosed: boolean;
  selectedChildren?: Record<string, Array<number>>;
}

const defaultDepthValue = 0; //parent branch, increasing value of depth = additional 'columns' in the tree
const depthSpacing = 3;
const searchDebounceTime = 300; //in ms
export default function DepartmentFilterSection(props: ISectionProps) {
  const { depth = defaultDepthValue } = props;
  const [showSection, setShowSection] = useState<ISectionState>({
    isSectionClosed: false,
    selectedChildren: {},
  });

  const [debouncedSearch, setDebouncedSearch] = useState(props.search);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(props.search);
    }, searchDebounceTime);

    return () => clearTimeout(timeout);
  }, [props.search]);

  function handleShow() {
    setShowSection(prev => {
      const nextIsClosed = !prev.isSectionClosed;
      return { isSectionClosed: nextIsClosed };
    });
  }

  function hasSelectedInTree(node: IDepartmentTree, selectedIds: number[]): boolean {
    if (selectedIds.includes(node.id)) {
      return true;
    }

    if (node.children && node.children.length > 0) {
      return node.children.some(child => hasSelectedInTree(child, selectedIds));
    }

    return false;
  }

  const matchesSearch = (department: IDepartmentTree) =>
    debouncedSearch !== "" &&
    [department.title, department.type].some(field => field?.toLowerCase().includes(debouncedSearch?.toLowerCase()));

  function renderChildren(nodes?: IDepartmentTree[], parentClosed = false) {
    if (!nodes) {
      return null;
    }

    const closed = parentClosed || showSection.isSectionClosed;

    return nodes
      .filter(node => !closed || hasSelectedInTree(node, props.deptIds))
      .map(node => (
        <DepartmentFilterSection
          key={node.id}
          onSelect={props.onSelect}
          search={props.search}
          type={node.type}
          section={node}
          depth={depth + depthSpacing}
          deptIds={props.deptIds}
          isClosed={closed}
        />
      ));
  }

  return (
    <>
      <div>
        {props.section ? (
          <div
            className={classNames("flex flex-row mb-1", {
              "search-found": matchesSearch(props.section),
            })}
            style={{ paddingLeft: 8 * depth }}
          >
            <ButtonNew type="tertiary" onClick={() => handleShow()}>
              {props.section.children.length !== 0 ? (
                <FontAwesomeIcon
                  className="mr-1 mb-1"
                  size="sm"
                  icon={showSection.isSectionClosed ? faChevronDown : faChevronRight}
                />
              ) : null}

              <div
                className={classNames("flex flex-row", {
                  "ml-6": props.section.children.length === 0,
                  "ml-1": props.section.children.length > 0,
                })}
              >
                <div onClick={e => e.stopPropagation()}>
                  <Checkbox
                    onChange={e => props.onSelect(e)}
                    id={props.section.type}
                    value={props.section.id}
                    checked={props.deptIds.includes(props.section.id)}
                    size="medium"
                  />
                </div>
                <div>{props.section.title}</div>
              </div>
            </ButtonNew>
          </div>
        ) : null}

        {renderChildren(props.section.children, props.isClosed ?? false)}
      </div>
    </>
  );
}
